import React, { useState, useEffect, useRef } from 'react';
import { MdMicNone, MdMic } from "react-icons/md";
import axios from 'axios';
import useAuth from '../../Users/hooks/useAuth';
import PacmanLoader from "react-spinners/PacmanLoader";
import Paginator from './Paginator';

const scoreColor = (score) => {
  if (typeof score !== 'number') return 'bg-gray-50 border-gray-200 text-gray-700';
  if (score >= 80) return 'bg-green-50 border-green-200 text-green-800';
  if (score >= 40) return 'bg-yellow-50 border-yellow-200 text-yellow-800';
  return 'bg-red-50 border-red-200 text-red-800';
};

const scoreBadge = (score) => {
  if (typeof score !== 'number') return 'bg-gray-400';
  if (score >= 80) return 'bg-green-600';
  if (score >= 40) return 'bg-yellow-600';
  return 'bg-red-600';
};

const phonemeDot = (score) => {
  if (typeof score !== 'number') return 'bg-gray-400 border-gray-500';
  if (score >= 80) return 'bg-green-500 border-green-600';
  if (score >= 40) return 'bg-yellow-500 border-yellow-600';
  return 'bg-red-500 border-red-600';
};

const formatKey = (key) => key.replace(/([A-Z])/g, ' $1').trim();

const metricTone = (value) => {
  if (typeof value !== 'number') return "text-blue-700";
  if (value >= 80) return "text-green-700";
  if (value >= 40) return "text-yellow-700";
  return "text-red-700";
};

const Slideshow = ({ transcript, title, version, language, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [segment, setSegment] = useState();
  const [isgranted, setIsGranted] = useState(false);
  const [stream, setStream] = useState();
  const [audio, setAudio] = useState(null);
  const [buttonState, setButtonState] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const { auth } = useAuth();
  const [segments, setSegments] = useState();
  const [maxpage, setMaxPage] = useState(1);
  const [page, setPage] = useState(1);
  const [recordingStatus, setRecordingStatus] = useState("inactive");

  // UI helpers
  const [sortMode, setSortMode] = useState("worst"); // "worst" | "original"
  const [showOnlyErrors, setShowOnlyErrors] = useState(false); // show only <80?
  const [viewMode, setViewMode] = useState("auto"); // "auto" | "chips" | "grid"

  const mediaRecorder = useRef(null);
  const audioChunksRef = useRef([]);
  const recordRef = useRef(null);
  const pulseRef = useRef(null);
  const recordingTimerRef = useRef(null);

  const sendBlob = React.useCallback(({ blob, title, version, language, index, segment }) => {
    if (loading) return;

    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_USER_API_URL}/analysis/upload_blob_presentation/${language}/${title}/${version}/${index}`;
    const formData = new FormData();

    formData.append('uploaded_file', blob);
    formData.append('sendText', JSON.stringify({ reference: segment }));
    formData.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);

    const config = {
      headers: {
        'content-type': blob.type,
        Authorization: `Bearer ${token}`
      },
    };

    setLoading(true);

    axios.post(url, formData, config)
      .then((response) => setAssessment(response.data))
      .catch((error) => {
        console.error("Error uploading file: ", error);
        setAssessment(null);
      })
      .finally(() => setLoading(false));
  }, [loading]);

  const pulse_frames = [{ transform: "scale(1.2)" }, { transform: "scale(1.0)" }];

  const progress_frames = [
    { transform: "scale(1.05)", color: "inherit" },
    { transform: "translateX(160px)", color: "inherit" },
    { transform: "scale(1.0)", color: "inherit" },
  ];

  const getMicrophonePermission = async () => {
    if ("MediaRecorder" in window) {
      try {
        const streamData = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        setIsGranted(true);
        setStream(streamData);
      } catch (err) {
        console.error("Microphone permission error:", err.message);
      }
    }
  };

  useEffect(() => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(streamData => {
          setIsGranted(true);
          setStream(streamData);
        })
        .catch(err => {
          console.log("Microphone permission not granted:", err.message);
        });
    }
  }, []);

  useEffect(() => {
    if (transcript?.segments) {
      setSegments(transcript.segments);
      setPage(1);
      setMaxPage(transcript.segments.length);
    }
  }, [transcript, version, language, title]);

  useEffect(() => {
    if (segments && page) {
      setSegment(segments[page - 1]);
      setAssessment(null);
      setAudio(null);
    }
  }, [page, segments, version]);

  const startRecording = async () => {
    if (!stream || recordingStatus === "recording") return;

    setRecordingStatus("recording");
    setButtonState(true);
    setAssessment(null);

    audioChunksRef.current = [];

    const media = new MediaRecorder(stream, { type: "audio/webm" });
    mediaRecorder.current = media;
    mediaRecorder.current.start();

    mediaRecorder.current.ondataavailable = (event) => {
      if (!event?.data || event.data.size === 0) return;
      audioChunksRef.current.push(event.data);
    };

    recordingTimerRef.current = setTimeout(() => stopRecording(), 30000);

    if (recordRef.current) {
      recordRef.current.animate(progress_frames, { duration: 30000, fill: "forwards", iterations: 1 });
    }
    if (pulseRef.current) {
      pulseRef.current.animate(pulse_frames, { duration: 500, iterations: Infinity });
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && recordingStatus === "recording") {
      setRecordingStatus("inactive");
      setButtonState(false);

      if (recordingTimerRef.current) clearTimeout(recordingTimerRef.current);

      if (recordRef.current) recordRef.current.getAnimations().forEach(animation => animation.cancel());
      if (pulseRef.current) pulseRef.current.getAnimations().forEach(animation => animation.cancel());

      mediaRecorder.current.stop();
      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const audioUrl = URL.createObjectURL(audioBlob);

        sendBlob({ blob: audioBlob, language, title, version, segment, index: page });

        setAudio(audioUrl);
        audioChunksRef.current = [];
      };
    }
  };

  const playRecording = () => {
    if (!audio) return;
    const audioElement = new Audio(audio);
    audioElement.play().catch(error => console.error("Error playing audio:", error));
  };

  // Support both shapes
  const userNBest =
    assessment?.user?.NBest?.[0] ??
    assessment?.pronunciation?.NBest?.[0] ??
    null;

  const hasUserAssessment = !!userNBest?.PronunciationAssessment;

  const wordsRaw = Array.isArray(userNBest?.Words) ? userNBest.Words : [];
  const wordCount = wordsRaw.length;
  const isLong = wordCount > 10;

  const wordItems = wordsRaw
    .map((w, idx) => ({ w, idx }))
    .filter(({ w }) => w?.PronunciationAssessment?.ErrorType !== 'Omission');

  const filtered = showOnlyErrors
    ? wordItems.filter(({ w }) => (w?.PronunciationAssessment?.AccuracyScore ?? 0) < 80)
    : wordItems;

  const sorted = sortMode === "worst"
    ? [...filtered].sort((a, b) => (a.w?.PronunciationAssessment?.AccuracyScore ?? 999) - (b.w?.PronunciationAssessment?.AccuracyScore ?? 999))
    : filtered;

  const effectiveView =
    viewMode === "auto"
      ? (isLong ? "chips" : "grid")
      : viewMode;

  return (
    // FULL PAGE
    <div className="min-h-screen w-full bg-gray-50">
      <div className="w-full max-w-6xl mx-auto px-4 py-4">
        {/* Top header bar */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>

            <div className="flex-1 text-center">
              <h2 className="text-lg font-bold text-gray-900 leading-tight">{title}</h2>
              <p className="text-xs text-gray-600">V{version} • {language}</p>
            </div>

            <div className="w-[88px]" />
          </div>

          {/* paginator row */}
          <div className="mt-3">
            <Paginator maxpage={maxpage} page={page} clickHandler={(p) => setPage(p)} />
          </div>
        </div>

        {/* Row 1: Current text */}
        <div className="mt-4 bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Current Text</h3>
            <div className="text-xs text-gray-500">Slide {page}/{maxpage}</div>
          </div>
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-2xl p-4">
            <p className="text-gray-900 text-base leading-relaxed">
              {segment || "No segment available"}
            </p>
          </div>
        </div>

        {/* Row 2: Recording */}
        <div className="mt-4 bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Record & Submit</h3>
            <div className="text-xs text-gray-500">
              {recordingStatus === "recording" ? "Recording (max 30s)" : "Ready"}
            </div>
          </div>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Mic + progress */}
            <div className="flex items-center gap-3">
              <div className="text-3xl text-blue-600">
                {recordingStatus === "inactive" ? <MdMicNone /> : <div ref={pulseRef}><MdMic /></div>}
              </div>

              <div className="w-64 max-w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div ref={recordRef} className="h-full bg-blue-600 rounded-full transition-all duration-300" />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-2">
              {recordingStatus === "inactive" ? (
                <button
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                  onClick={startRecording}
                  disabled={loading || !isgranted}
                >
                  Record
                </button>
              ) : (
                <button
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 transition-colors"
                  onClick={stopRecording}
                >
                  Stop
                </button>
              )}

              <button
                className={`px-4 py-2 text-sm rounded-xl transition-colors ${
                  !audio || buttonState
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
                onClick={playRecording}
                disabled={!audio || buttonState}
              >
                Play
              </button>

              {!isgranted && (
                <button
                  className="px-4 py-2 bg-gray-900 text-white text-sm rounded-xl hover:bg-black transition-colors"
                  onClick={getMicrophonePermission}
                >
                  Enable Mic
                </button>
              )}
            </div>
          </div>

          {recordingStatus === "recording" && (
            <div className="mt-3 text-xs text-gray-600 animate-pulse">
              Recording… speak naturally, then press Stop (or wait 30s).
            </div>
          )}
        </div>

        {/* Row 3: Assessment */}
        <div className="mt-4 bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">🎤</span>
              <div>
                <div className="font-semibold text-gray-900">Your AI Speech Assessment</div>
                <div className="text-xs text-gray-500">
                  Phoneme dots = per-sound accuracy (green good, yellow ok, red weak)
                </div>
              </div>
            </div>

            {/* Controls sit in the same top row */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isLong && (
                <>
                  <button
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      sortMode === "worst" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() => setSortMode("worst")}
                  >
                    Worst first
                  </button>
                  <button
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      sortMode === "original" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() => setSortMode("original")}
                  >
                    Original
                  </button>
                  <button
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      showOnlyErrors ? "bg-red-50 text-red-700 border-red-200" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() => setShowOnlyErrors(v => !v)}
                    title="Show only words with score < 80"
                  >
                    {showOnlyErrors ? "Only <80" : "All words"}
                  </button>
                </>
              )}

              <button
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  viewMode === "auto" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
                onClick={() => setViewMode("auto")}
                title="Auto chooses best layout for short/long"
              >
                Auto
              </button>
              <button
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  viewMode === "chips" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
                onClick={() => setViewMode("chips")}
              >
                Chips
              </button>
              <button
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  viewMode === "grid" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
                onClick={() => setViewMode("grid")}
              >
                Grid
              </button>
            </div>
          </div>

          {/* Guest note */}
          {auth.username === 'guest' && (
            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-2xl p-3 text-sm text-yellow-800">
              AI assessment available for registered users.
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="mt-4 flex flex-col items-center justify-center p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <PacmanLoader loading={loading} color="#3B82F6" size={15} />
              <span className="mt-2 text-gray-600 text-sm">Analyzing...</span>
            </div>
          )}

          {/* Results */}
          {!loading && auth.username !== 'guest' && (
            <div className="mt-4">
              {!hasUserAssessment ? (
                <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-2xl border border-gray-200">
                  <p>No pronunciation assessment data available.</p>
                  <p className="text-sm mt-2">Record to see phoneme-level feedback.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Overall row (NOT sidebar) */}
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-gray-900">Overall</div>
                      <div className="text-xs text-gray-500">
                        {wordCount} word{wordCount === 1 ? '' : 's'}
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(userNBest.PronunciationAssessment || {}).map(([key, value]) => (
                        <div key={key} className="bg-white border border-gray-200 rounded-xl px-3 py-2">
                          <div className="text-[11px] text-gray-500">{formatKey(key)}</div>
                          <div className={`text-sm font-semibold ${metricTone(value)}`}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Words row */}
                  {effectiveView === "chips" ? (
                    // CHIP ROW for long sentences: horizontally scrollable
                    <div className="bg-white border border-gray-200 rounded-2xl p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">
                        Word-by-word
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          (scroll →)
                        </span>
                      </div>

                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {sorted.map(({ w, idx }) => {
                          const s = w?.PronunciationAssessment?.AccuracyScore;

                          return (
                            <div
                              key={`${idx}-${w?.Word}`}
                              className={`min-w-[160px] max-w-[220px] flex-shrink-0 rounded-2xl border p-3 ${scoreColor(s)}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-semibold text-sm leading-tight break-words">
                                  {w?.Word}
                                </div>
                                <div className={`shrink-0 text-white text-[11px] font-bold px-2 py-0.5 rounded-full ${scoreBadge(s)}`}>
                                  {typeof s === 'number' ? s : 'N/A'}
                                </div>
                              </div>

                              {Array.isArray(w?.Phonemes) && w.Phonemes.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {w.Phonemes.map((ph, j) => {
                                    const ps = ph?.PronunciationAssessment?.AccuracyScore;
                                    return (
                                      <div
                                        key={j}
                                        className={`h-2.5 w-2.5 border rounded-full ${phonemeDot(ps)}`}
                                        title={`${ph?.Phoneme}: ${typeof ps === 'number' ? ps : 'N/A'}`}
                                      />
                                    );
                                  })}
                                </div>
                              )}

                              <div className="mt-2 text-[11px] text-gray-600 flex items-center justify-between">
                                <span className="opacity-80">
                                  {w?.PronunciationAssessment?.ErrorType && w.PronunciationAssessment.ErrorType !== "None"
                                    ? w.PronunciationAssessment.ErrorType
                                    : "—"}
                                </span>
                                <span className="opacity-80">
                                  #{idx + 1}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    // GRID for short/medium: clean and centered
                    <div className="bg-white border border-gray-200 rounded-2xl p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Word-by-word</div>

                      <div className={`grid gap-2 ${
                        isLong
                          ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
                          : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
                      }`}>
                        {sorted.map(({ w, idx }) => {
                          const s = w?.PronunciationAssessment?.AccuracyScore;

                          return (
                            <div
                              key={`${idx}-${w?.Word}`}
                              className={`rounded-2xl border p-3 ${scoreColor(s)} hover:shadow-sm transition-shadow`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-semibold text-sm leading-tight break-words">
                                  {w?.Word}
                                </div>
                                <div className={`shrink-0 text-white text-[11px] font-bold px-2 py-0.5 rounded-full ${scoreBadge(s)}`}>
                                  {typeof s === 'number' ? s : 'N/A'}
                                </div>
                              </div>

                              {Array.isArray(w?.Phonemes) && w.Phonemes.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {w.Phonemes.map((ph, j) => {
                                    const ps = ph?.PronunciationAssessment?.AccuracyScore;
                                    return (
                                      <div
                                        key={j}
                                        className={`h-2.5 w-2.5 border rounded-full ${phonemeDot(ps)}`}
                                        title={`${ph?.Phoneme}: ${typeof ps === 'number' ? ps : 'N/A'}`}
                                      />
                                    );
                                  })}
                                </div>
                              )}

                              <div className="mt-2 text-[11px] text-gray-600 flex items-center justify-between">
                                <span className="opacity-80">
                                  {w?.PronunciationAssessment?.ErrorType && w.PronunciationAssessment.ErrorType !== "None"
                                    ? w.PronunciationAssessment.ErrorType
                                    : "—"}
                                </span>
                                <span className="opacity-80">
                                  #{idx + 1}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {sorted.length === 0 && (
                        <div className="text-center text-sm text-gray-500 py-8">
                          Nothing to show (try “All words”).
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom padding */}
        <div className="h-6" />
      </div>
    </div>
  );
};

export default Slideshow;
